import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'

/**
 * Um cliente para o app inteiro.
 *
 * `retry: 1` porque a maior parte das falhas aqui é de rede e uma segunda
 * tentativa resolve. Erro de validação ou de permissão não melhora tentando,
 * e quem decide isso é o `ApiError` em `lib/api.ts`, que não repete 4xx.
 *
 * `staleTime` de 30 s vale para dado de agenda e de cobrança. Dado clínico
 * decifrado não pode ficar em memória depois que a tela fecha: essas queries
 * passam `gcTime: 0` por query, na onda que traz o prontuário.
 */
/**
 * 4xx não melhora tentando de novo. O teste é estrutural em vez de um
 * `instanceof ApiError` para não fazer `lib/api.ts` e este arquivo
 * dependerem um do outro.
 */
function isClientError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false
  const status = (error as { status?: unknown }).status
  return typeof status === 'number' && status >= 400 && status < 500
}

type AvisoDeErro = (error: unknown, origem: 'query' | 'mutation') => void

/**
 * Quem mostra o erro que nenhuma tela tratou. Entra por registro, em
 * `routes/__root.tsx`, pelo mesmo motivo do teste estrutural acima.
 */
let avisarErro: AvisoDeErro = () => undefined

export function setAvisoDeErro(aviso: AvisoDeErro): void {
  avisarErro = aviso
}

function silencioso(meta: Record<string, unknown> | undefined): boolean {
  return meta?.silencioso === true
}

export const queryClient = new QueryClient({
  // Query sem dado ainda mostra `Failure` na própria tela; o aviso é só para
  // a atualização em segundo plano que falhou por cima de dado que já estava lá.
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query.state.data === undefined || silencioso(query.meta)) return
      avisarErro(error, 'query')
    },
  }),
  // Mutação com `onError` próprio já cuida do erro; as outras falhariam caladas.
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (mutation.options.onError !== undefined || silencioso(mutation.meta)) return
      avisarErro(error, 'mutation')
    },
  }),
  defaultOptions: {
    queries: {
      retry: (attempt, error) => attempt < 1 && !isClientError(error),
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
})

/** Chave da sessão. Um 401 em qualquer lugar invalida esta. */
export const sessionKey = ['session'] as const
